import { c as _c } from "react/compiler-runtime";
// Центр помощи покупателя (fd-support-01): FAQ по кадру «Поддержка» 4052:41704
// (Айталы, 02.10) — чипы категорий, группы и вопросы-аккордеоны; вопросы и
// категории ведёт админка (faq_articles, аудитория «покупатели»). Раздел
// «Мои обращения» (список, создание тикета, тред, закрытие) остаётся: в кадре
// его нет, открывается неброской ссылкой под FAQ (вопрос заказчику, A-36).
import { ArrowLeft, LifeBuoy, MessageSquarePlus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import type { SupportTicketStatus } from "@/api/types";
import { useAddTicketMessage, useCloseTicket, useCreateTicket, useSupportFaq, useSupportTicket, useSupportTickets } from "@/api/use-support";
import { EmptyState, ErrorState } from "@/components/common/states";
import { ChevronDownIcon, ChevronUpIcon, SendIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { TextField } from "@/components/ui/text-field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Skeleton } from "@osmix/ui/skeleton";
import { Accordion } from "radix-ui";
const TICKET_CATEGORIES = ["general", "order", "payment", "delivery", "return", "other"] as const;
const STATUS_STYLE: Record<SupportTicketStatus, string> = {
  open: "bg-status-draft/10 text-status-draft",
  in_progress: "bg-osmix-tangelo/10 text-osmix-tangelo",
  resolved: "bg-status-published/10 text-status-published",
  closed: "bg-osmix-gray-100 text-osmix-text-2"
};
function StatusBadge(t0) {
  const $ = _c(8);
  const {
    status
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = STATUS_STYLE[status];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex h-6 items-center rounded-full px-3 type-small font-bold", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `support.status.${status}`;
  let t4;
  if ($[2] !== t || $[3] !== t3) {
    t4 = t(t3);
    $[2] = t;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <span className={t2}>{t4}</span>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

// ── FAQ ──────────────────────────────────────────────────────────────────────
// Кадр 4052:41704: чипы DS `Chip` sm (h32 r96, 14/18 w500) через 12 — активный
// #FA5C2E 5 % с обводкой #FA5C2E 0.5, остальные #1F1F1F 3 %; группы через 16 —
// заголовок 16/20 w600, вопросы через 12: белая строка 56 r12 (p16, 14/18),
// шеврон Chevron 24 (вниз/вверх); раскрытый ответ — под строкой на сером фоне (p12/16), шаги
// «Шаг N» 14/18 w600, текст 14/18, через 12.
// `wrapChips` — в кабинете чипы переносятся строками через 8 (кадр 4118:44924),
// на публичной странице телефона — лента с прокруткой.
export function FaqSection(t0) {
  const $ = _c(38);
  const {
    wrapChips: t1
  } = t0 === undefined ? {} : t0;
  const wrapChips = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const [category, setCategory] = useState(null);
  const faq = useSupportFaq();
  let t2;
  if ($[0] !== faq.data?.groups) {
    t2 = faq.data?.groups ?? [];
    $[0] = faq.data?.groups;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const groups = t2;
  let t3;
  if ($[2] !== category || $[3] !== groups) {
    t3 = category ? groups.filter(g => g.category === category) : groups;
    $[2] = category;
    $[3] = groups;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const shown = t3;
  if (faq.isPending) {
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton className="h-64 w-full rounded-[12px] bg-white" />;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (faq.isError) {
    let t4;
    if ($[6] !== faq) {
      t4 = <ErrorState onRetry={() => void faq.refetch()} />;
      $[6] = faq;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (groups.length === 0) {
    let t4;
    if ($[8] !== t) {
      t4 = t("support.faqEmpty");
      $[8] = t;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t4) {
      t5 = <EmptyState icon={LifeBuoy} title={t4} />;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  const t4 = wrapChips ? "flex-wrap gap-2 md:gap-3" : "-mx-4 gap-3 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:flex-wrap md:px-0";
  let t5;
  if ($[12] !== t4) {
    t5 = cn("flex", t4);
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("support.tabFaq");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = category === null;
  const t8 = category === null;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setCategory(null);
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("support.allQuestions", {
      defaultValue: "\u0412\u0441\u0435 \u0432\u043E\u043F\u0440\u043E\u0441\u044B"
    });
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7 || $[21] !== t8) {
    t11 = <Chip role="tab" aria-selected={t7} selected={t8} onClick={t9}>{t10}</Chip>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== category || $[24] !== groups) {
    let t13;
    if ($[26] !== category) {
      t13 = g_0 => <Chip key={g_0.category} role="tab" aria-selected={category === g_0.category} selected={category === g_0.category} onClick={() => setCategory(g_0.category)}>{g_0.category}</Chip>;
      $[26] = category;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    t12 = groups.map(t13);
    $[23] = category;
    $[24] = groups;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12 || $[30] !== t5 || $[31] !== t6) {
    t13 = <div className={t5} role="tablist" aria-label={t6}>{t11}{t12}</div>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t5;
    $[31] = t6;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== shown) {
    t14 = shown.map(_temp2);
    $[33] = shown;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = <div className="flex flex-col gap-4">{t13}{t14}</div>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
}

// Ответ из админки — обычный текст. Строка «Шаг N» начинает шаг (подзаголовок
// w600, под ним текст через 6); прочие абзацы — просто текст.
function _temp2(group) {
  return <section key={group.category} className="flex flex-col gap-3"><h2 className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#1F1F1F]">{group.category}</h2><Accordion.Root type="single" collapsible={true} className="flex flex-col gap-3">{group.articles.map(_temp)}</Accordion.Root></section>;
}
function _temp(article) {
  return <Accordion.Item key={article.id} value={article.id}><Accordion.Header className="flex"><Accordion.Trigger className="group/faq flex flex-1 items-center justify-between gap-4 rounded-[12px] bg-white p-4 text-left text-[14px] leading-[18px] text-ds-primary outline-none transition-colors hover:bg-white/70 focus-visible:ring-2 focus-visible:ring-ds-focus">{article.question}<ChevronDownIcon size={24} className="shrink-0 group-data-[state=open]/faq:hidden" /><ChevronUpIcon size={24} className="hidden shrink-0 group-data-[state=open]/faq:block" /></Accordion.Trigger></Accordion.Header><Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"><div className="px-4 py-3"><FaqAnswer text={article.answer} /></div></Accordion.Content></Accordion.Item>;
}
function FaqAnswer(t0) {
  const $ = _c(2);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    const blocks = [];
    for (const raw of text.split("\n")) {
      const line = raw.trim();
      if (!line) {
        if (blocks.at(-1)?.lines.length) {
          blocks.push({
            lines: []
          });
        }
        continue;
      }
      if (/^шаг\s*\d+[.:)]?$/i.test(line)) {
        blocks.push({
          title: line,
          lines: []
        });
      } else {
        if (blocks.length === 0) {
          blocks.push({
            lines: [line]
          });
        } else {
          blocks.at(-1)?.lines.push(line);
        }
      }
    }
    t1 = <div className="flex flex-col gap-3 text-[14px] leading-[18px] text-[#1F1F1F]">{blocks.filter(_temp3).map(_temp4)}</div>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// Под FAQ — неброские ссылки: живой чат с поддержкой и прежние обращения.
function _temp4(b_0, i) {
  return <div key={`${b_0.title ?? "p"}-${i}`} className="flex flex-col gap-1.5">{b_0.title && <p className="font-semibold">{b_0.title}</p>}{b_0.lines.length > 0 && <p className="whitespace-pre-line">{b_0.lines.join("\n")}</p>}</div>;
}
function _temp3(b) {
  return b.title || b.lines.length > 0;
}
export function SupportLinks() {
  const $ = _c(11);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = t("support.openChat", {
      defaultValue: "\u041D\u0430\u043F\u0438\u0441\u0430\u0442\u044C \u0432 \u0447\u0430\u0442 \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0438"
    });
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <Link to="/chats" className="rounded-sm text-[14px] leading-[18px] text-[#1F1F1F]/64 outline-none hover:text-[#1F1F1F] hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{t0}</Link>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("support.myTickets");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <Link to="/profile/support?view=tickets" className="rounded-sm text-[14px] leading-[18px] text-[#1F1F1F]/64 outline-none hover:text-[#1F1F1F] hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{t2}</Link>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2">{t1}{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

// ── Обращения ────────────────────────────────────────────────────────────────
export function TicketsSection() {
  const $ = _c(5);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  if (creating) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <CreateTicketForm onClose={() => setCreating(false)} onCreated={id => {
        setCreating(false);
        setSelectedId(id);
      }} />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (selectedId) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = () => setSelectedId(null);
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== selectedId) {
      t1 = <TicketThread id={selectedId} onBack={t0} />;
      $[2] = selectedId;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t0;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TicketList onSelect={setSelectedId} onCreate={() => setCreating(true)} />;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}
function TicketList(t0) {
  const $ = _c(20);
  const {
    onSelect,
    onCreate
  } = t0;
  const {
    t
  } = useTranslation();
  const tickets = useSupportTickets();
  let t1;
  if ($[0] !== t) {
    t1 = t("support.openChat", {
      defaultValue: "\u041D\u0430\u043F\u0438\u0441\u0430\u0442\u044C \u0432 \u0447\u0430\u0442 \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0438"
    });
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Button variant="gray" size="md" asChild={true}><Link to="/chats">{t1}</Link></Button>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <MessageSquarePlus className="size-4" aria-hidden="true" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("support.newTicket");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onCreate || $[8] !== t4) {
    t5 = <Button size="md" onClick={onCreate}>{t3}{t4}</Button>;
    $[7] = onCreate;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <div className="flex flex-wrap items-center justify-end gap-3"><div className="flex flex-wrap items-center gap-2">{t2}{t5}</div></div>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== onSelect || $[14] !== t || $[15] !== tickets) {
    t7 = tickets.isPending ? <Skeleton className="h-40 w-full rounded-2xl" /> : tickets.isError ? <ErrorState onRetry={() => void tickets.refetch()} /> : (tickets.data?.length ?? 0) === 0 ? <EmptyState icon={LifeBuoy} title={t("support.ticketsEmpty")} hint={t("support.ticketsEmptyHint")} className="rounded-[16px] bg-white" /> : <ul className="flex flex-col gap-3">{tickets.data?.map(ticket => <li key={ticket.id}><button type="button" onClick={() => onSelect(ticket.id)} className="flex w-full flex-col gap-2 rounded-[16px] bg-white p-4 text-left transition-colors hover:bg-white/70 sm:p-5"><div className="flex items-start justify-between gap-3"><h3 className="type-body1-bold text-osmix-text-1">{ticket.subject}</h3><StatusBadge status={ticket.status} /></div><div className="flex flex-wrap items-center gap-x-3 gap-y-1 type-small text-osmix-text-2"><span>{ticket.category}</span><span aria-hidden="true">•</span><span>{t("support.messageCount", {
                count: ticket.messageCount
              })}</span><span aria-hidden="true">•</span><time dateTime={ticket.lastMessageAt}>{new Date(ticket.lastMessageAt).toLocaleString()}</time></div></button></li>)}</ul>;
    $[13] = onSelect;
    $[14] = t;
    $[15] = tickets;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="flex flex-col gap-4">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function CreateTicketForm(t0) {
  const $ = _c(81);
  const {
    onClose,
    onCreated
  } = t0;
  const {
    t
  } = useTranslation();
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const create = useCreateTicket();
  let t1;
  if ($[0] !== category || $[1] !== create || $[2] !== message || $[3] !== onCreated || $[4] !== subject || $[5] !== t) {
    t1 = () => {
      if (!subject.trim() || !message.trim()) {
        return;
      }
      create.mutate({
        subject: subject.trim(),
        category,
        message: message.trim()
      }, {
        onSuccess: ticket => {
          toast.success(t("support.ticketCreated"));
          onCreated(ticket.id);
        },
        onError: () => toast.error(t("support.ticketError"))
      });
    };
    $[0] = category;
    $[1] = create;
    $[2] = message;
    $[3] = onCreated;
    $[4] = subject;
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const submit = t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowLeft className="size-4" aria-hidden="true" />;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t) {
    t3 = t("support.backToTickets");
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== onClose || $[11] !== t3) {
    t4 = <Button variant="text" onClick={onClose} className="gap-1.5 self-start">{t2}{t3}</Button>;
    $[10] = onClose;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t) {
    t5 = t("support.newTicket");
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <h2 className="type-h3-bold text-osmix-text-1">{t5}</h2>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t) {
    t7 = t("support.subject");
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e => setSubject(e.target.value);
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t("support.subjectPlaceholder");
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== subject || $[23] !== t7 || $[24] !== t9) {
    t10 = <TextField size="lg" label={t7} value={subject} onChange={t8} maxLength={300} placeholder={t9} />;
    $[22] = subject;
    $[23] = t7;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t) {
    t11 = t("support.category");
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11) {
    t12 = <span className="text-[14px] leading-[18px] font-medium text-ds-label">{t11}</span>;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t) {
    t14 = TICKET_CATEGORIES.map(c => <SelectItem key={c} value={c}>{t(`support.categories.${c}`)}</SelectItem>);
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14) {
    t15 = <SelectContent>{t14}</SelectContent>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== category || $[36] !== t15) {
    t16 = <Select value={category} onValueChange={setCategory}>{t13}{t15}</Select>;
    $[35] = category;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t12 || $[39] !== t16) {
    t17 = <label className="flex flex-col gap-1.5">{t12}{t16}</label>;
    $[38] = t12;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t) {
    t18 = t("support.message");
    $[41] = t;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t18) {
    t19 = <span className="text-[14px] leading-[18px] font-medium text-ds-label">{t18}</span>;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e_0 => setMessage(e_0.target.value);
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t) {
    t21 = t("support.messagePlaceholder");
    $[46] = t;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== message || $[49] !== t21) {
    t22 = <Textarea value={message} onChange={t20} rows={5} maxLength={10000} placeholder={t21} />;
    $[48] = message;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t19 || $[52] !== t22) {
    t23 = <label className="flex flex-col gap-1.5">{t19}{t22}</label>;
    $[51] = t19;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== create || $[55] !== message || $[56] !== subject) {
    t24 = !subject.trim() || !message.trim() || create.isPending;
    $[54] = create;
    $[55] = message;
    $[56] = subject;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t) {
    t25 = t("support.send");
    $[58] = t;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== submit || $[61] !== t24 || $[62] !== t25) {
    t26 = <Button size="md" disabled={t24} onClick={submit}>{t25}</Button>;
    $[60] = submit;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t) {
    t27 = t("common.cancel");
    $[64] = t;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== onClose || $[67] !== t27) {
    t28 = <Button variant="gray" size="md" onClick={onClose}>{t27}</Button>;
    $[66] = onClose;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== t26 || $[70] !== t28) {
    t29 = <div className="flex gap-3">{t26}{t28}</div>;
    $[69] = t26;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== t10 || $[73] !== t17 || $[74] !== t23 || $[75] !== t29) {
    t30 = <div className="flex flex-col gap-4 rounded-[16px] bg-white p-4 sm:p-5">{t10}{t17}{t23}{t29}</div>;
    $[72] = t10;
    $[73] = t17;
    $[74] = t23;
    $[75] = t29;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t30 || $[78] !== t4 || $[79] !== t6) {
    t31 = <div className="flex flex-col gap-4">{t4}{t6}{t30}</div>;
    $[77] = t30;
    $[78] = t4;
    $[79] = t6;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  return t31;
}
function TicketThread(t0) {
  const $ = _c(20);
  const {
    id,
    onBack
  } = t0;
  const {
    t
  } = useTranslation();
  const ticket = useSupportTicket(id);
  const addMessage = useAddTicketMessage(id);
  const closeTicket = useCloseTicket(id);
  const [reply, setReply] = useState("");
  let t1;
  if ($[0] !== addMessage || $[1] !== reply || $[2] !== t) {
    t1 = () => {
      if (!reply.trim()) {
        return;
      }
      addMessage.mutate(reply.trim(), {
        onSuccess: () => setReply(""),
        onError: () => toast.error(t("support.messageError"))
      });
    };
    $[0] = addMessage;
    $[1] = reply;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const send = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowLeft className="size-4" aria-hidden="true" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("support.backToTickets");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onBack || $[8] !== t3) {
    t4 = <Button variant="text" onClick={onBack} className="gap-1.5 self-start">{t2}{t3}</Button>;
    $[7] = onBack;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== addMessage || $[11] !== closeTicket || $[12] !== reply || $[13] !== send || $[14] !== t || $[15] !== ticket) {
    t5 = ticket.isPending ? <Skeleton className="h-80 w-full rounded-2xl" /> : ticket.isError || !ticket.data ? <ErrorState onRetry={() => void ticket.refetch()} /> : <><div className="flex items-start justify-between gap-3"><div><h2 className="type-h3-bold text-osmix-text-1">{ticket.data.subject}</h2><p className="mt-0.5 type-body2 text-osmix-text-2">{ticket.data.category}</p></div><StatusBadge status={ticket.data.status} /></div><ol className="flex flex-col gap-3">{ticket.data.messages.map(m => {
          const support = m.authorRole === "support";
          return <li key={m.id} className={cn("flex", support ? "justify-start" : "justify-end")}><div className={cn("max-w-[85%] rounded-2xl px-4 py-3 shadow-block", support ? "bg-osmix-gray-100 text-osmix-text-1" : "bg-osmix-tangelo text-white")}><div className={cn("type-small font-bold", support ? "text-osmix-text-2" : "text-white/80")}>{m.authorName ?? (support ? t("support.roleSupport") : t("support.roleYou"))}</div><p className="mt-1 whitespace-pre-line type-body2">{m.body}</p><time dateTime={m.createdAt} className={cn("mt-1 block type-small", support ? "text-osmix-icon" : "text-white/70")}>{new Date(m.createdAt).toLocaleString()}</time></div></li>;
        })}</ol>{ticket.data.status === "closed" ? <p className="rounded-xl bg-osmix-gray-100 px-4 py-3 text-center type-body2 text-osmix-text-2">{t("support.closedNotice")}</p> : <div className="flex flex-col gap-3 rounded-[16px] bg-white p-4"><Textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} maxLength={10000} placeholder={t("support.replyPlaceholder")} /><div className="flex items-center justify-between gap-3"><Button variant="gray" size="sm" disabled={closeTicket.isPending} onClick={() => closeTicket.mutate(undefined, {
            onSuccess: () => toast.success(t("support.closed")),
            onError: () => toast.error(t("support.closeError"))
          })}>{t("support.close")}</Button><Button size="md" disabled={!reply.trim() || addMessage.isPending} onClick={send}><SendIcon size={16} />{t("support.send")}</Button></div></div>}</>;
    $[10] = addMessage;
    $[11] = closeTicket;
    $[12] = reply;
    $[13] = send;
    $[14] = t;
    $[15] = ticket;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <div className="flex flex-col gap-4">{t4}{t5}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}

// Публичная страница /support (ссылка «Центр помощи» в подвале): тот же FAQ,
// что в разделе «Поддержка» кабинета, со своей шапкой. `embedded` — внутри
// кабинета: заголовок даёт панель кабинета, собственную шапку не рисуем.
export function SupportPage(t0) {
  const $ = _c(27);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    embedded: t2
  } = t1;
  const embedded = t2 === undefined ? false : t2;
  const {
    t
  } = useTranslation();
  const t3 = embedded ? "flex w-full flex-col" : "mx-auto flex w-full max-w-3xl flex-col gap-6 pt-2.5 md:gap-8";
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-white text-osmix-tangelo shadow-sm"><LifeBuoy className="size-6" aria-hidden="true" /></span>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t) {
    t5 = t("support.title");
    $[3] = t;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = <h1 className="type-h1-bold text-osmix-text-1">{t5}</h1>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t) {
    t7 = t("support.subtitle");
    $[7] = t;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t7) {
    t8 = <p className="mt-1 type-body2 text-osmix-text-2">{t7}</p>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <div className="flex items-start gap-4">{t4}<div>{t6}{t8}</div></div>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== embedded || $[15] !== t9) {
    t10 = <header hidden={embedded} className="overflow-hidden rounded-3xl bg-osmix-gray-100 px-5 py-6 sm:px-7 sm:py-8">{t9}</header>;
    $[14] = embedded;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = embedded ? "flex flex-col" : "flex flex-col rounded-[20px] bg-[#F8F8F8] p-4 md:p-5";
  let t12;
  if ($[17] !== embedded) {
    t12 = <FaqSection wrapChips={embedded} />;
    $[17] = embedded;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SupportLinks />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t11 || $[21] !== t12) {
    t14 = <div className={t11}>{t12}{t13}</div>;
    $[20] = t11;
    $[21] = t12;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t10 || $[24] !== t14 || $[25] !== t3) {
    t15 = <div className={t3}>{t10}{t14}</div>;
    $[23] = t10;
    $[24] = t14;
    $[25] = t3;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}